import {
	bindable,
	bindingMode,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { getZeroValueForDataType } from '~/models/data-type';

import { FieldModel, FormModel } from '~/models/form-model';

export interface FieldModelRow {
	[colPrefix: string]: FieldModel;
}

export interface DynamicFieldMeta {
	fieldNamePrefix: string;
	header?: string;
	hidden?: boolean;
	fieldType: 'datepicker' | 'text' | 'number' | 'select' | 'search' | 'user';
	/**
	 * When calculating whether a row is empty or not, force this field to be
	 * seen as empty.
	 * ie. INVESTMENT_TYPE.SPECIFIED_ORDER
	 */
	forceIsEmpty?: boolean;
	/**
	 * Wether a field is editable or not.
	 * ie. INVESTMENT_TYPE.SPECIFIED_ORDER
	 */
	readonly?: boolean;

	/* Button for field */
	button?: boolean;
	buttonImageClass?: string;
	onButtonClick?: (rowNum: number, totalRows: number) => void;

	onSearchSelect?: (
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	) => void;
}

/**
 * DynamicFieldTable is for editing rows of related fields
 * ie. RGz_Action_Desc01, RGs_Spare01, etc
 */
@customElement('dynamic-field-table')
@useView(PLATFORM.moduleName('./dynamic-field-table.html'))
export class DynamicFieldTable {
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private editMode: boolean = true;

	@bindable({
		defaultBindingMode: bindingMode.toView,
		changeHandler: 'formModelChanged',
	})
	protected formModel: FormModel;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	protected maxRows: number = 0;

	// If hideRowControls is true, then the controls to add or remove rows will be hidden
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	protected hideRowControls: boolean = false;

	@bindable({
		defaultBindingMode: bindingMode.toView,
		changeHandler: 'fieldMetaChanged',
	})
	protected fieldMeta: DynamicFieldMeta[] = [];

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	protected onAddRecord?: (rows: number, totalRows: number) => void;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	protected onRenderAdditionalInfo?: (rowIndex: number) => string;

	protected fieldModelMatrix: FieldModelRow[] = [];
	protected fieldAdditionalInfo: string[] = [];

	private attached(): void {
		if (!this.fieldModelMatrix || this.fieldModelMatrix.length === 0) {
			// NOTE: Jake: 2019-07-26 - WEBUI-2292
			// Setup for first bound vars.
			// This is required for Cypress tests to work as they
			// don't change "formModel" after the initial render.
			this.updateFieldModelMatrix();
		}
	}

	private formModelChanged(
		newFormModel: FormModel,
		oldFormModel: FormModel,
	): void {
		this.updateFieldModelMatrix();
	}

	private fieldMetaChanged(fieldMeta, oldFieldMeta): void {
		this.updateFieldModelMatrix();
	}

	protected updateFieldModelMatrix(): void {
		if (!this.formModel) {
			// Reset to empty if no form model
			this.fieldModelMatrix = [];
			return;
		}
		const fieldNameMatrix = this.computeFieldNameMatrix();

		// Update field model matrix
		// (only set once to avoid triggering multiple compute events)
		{
			const fieldModelMatrix = [];
			this.fieldAdditionalInfo = [];
			let rowIndex = 0;
			for (const row of fieldNameMatrix) {
				if (this.isEmptyRow(row)) {
					rowIndex++;
					continue;
				}
				this.pushRecord(fieldModelMatrix, rowIndex);
				if (this.onRenderAdditionalInfo) {
					this.fieldAdditionalInfo.push(
						this.onRenderAdditionalInfo(rowIndex),
					);
				}
				rowIndex++;
			}
			if (fieldModelMatrix.length === 0) {
				// NOTE: Jake: 2019-07-26 - WEBUI-2292
				// Always show at least 1 record
				this.pushRecord(fieldModelMatrix, 0);
				if (this.onRenderAdditionalInfo) {
					this.fieldAdditionalInfo.push(
						this.onRenderAdditionalInfo(0),
					);
				}
			}
			// NOTE: Jake: 2019-07-31
			// Set "this.fieldModelMatrix" after we've built up the list in another variable.
			// This ensures the any computations based on this variable occur less often.
			this.fieldModelMatrix = fieldModelMatrix;
		}
	}

	private computeFieldNameMatrix(): string[][] {
		// render a matrix (Z columns and N rows)
		// [[colA01, colB01..., colZ01],
		// [colA02, colB02..., colZ02],
		// [....                     ],
		// [colA0N, colB0N..., colZ0N]]
		//
		// rowNum starts with 0 but fields start with 1
		const fieldNameMatrix: string[][] = [];
		for (
			let fieldNameRowNumber = 1;
			fieldNameRowNumber <= this.maxRows;
			fieldNameRowNumber++
		) {
			const fieldNameRow: string[] = [];
			for (const meta of this.fieldMeta) {
				fieldNameRow.push(
					meta.fieldNamePrefix +
						DynamicFieldTable.getSuffix(fieldNameRowNumber),
				);
			}
			fieldNameMatrix.push(fieldNameRow);
		}

		if (!this.isValidFieldNameMatrix(fieldNameMatrix)) {
			throw Error(`some field names can't be found in formModel`);
		}
		return fieldNameMatrix;
	}

	public static getFieldModel(
		formModel: FormModel,
		fieldNamePrefix: string,
		row: number,
	): FieldModel | undefined {
		if (!formModel) {
			throw new Error(
				'Cannot query formModel for non-existent field: ' +
					fieldNamePrefix,
			);
		}
		// rowNum starts with 0 but fields start with 1
		const suffix: string = DynamicFieldTable.getSuffix(row + 1);
		const fieldName = fieldNamePrefix + suffix;
		return formModel.fields[fieldName];
	}

	protected onClickAddRecord(e): void {
		const row = this.pushRecordIntoFirstAvailableRow(this.fieldModelMatrix);
		if (this.onAddRecord) {
			this.onAddRecord(row, this.fieldModelMatrix.length);
		}
	}

	/**
	 * push record into fieldModelMatrix for the specified row index
	 */
	private pushRecord(
		fieldModelMatrix: FieldModelRow[],
		rowIndex: number,
	): void {
		if (rowIndex >= this.maxRows) {
			return;
		}
		const fieldModelRow: { [fieldName: string]: FieldModel } = {};
		for (const meta of this.fieldMeta) {
			fieldModelRow[meta.fieldNamePrefix] =
				DynamicFieldTable.getFieldModel(
					this.formModel,
					meta.fieldNamePrefix,
					rowIndex,
				);
		}
		fieldModelMatrix.push(fieldModelRow);
	}

	/**
	 * push record into fieldModelMatrix, finding the first available row index, and returns the index
	 * of the inserted row.
	 */
	private pushRecordIntoFirstAvailableRow(
		fieldModelMatrix: FieldModelRow[],
	): number {
		for (let rowIndex = 0; rowIndex < this.maxRows; rowIndex++) {
			let isRowAlreadyInFieldModel = false;

			// Check to see if this row already exists in the fieldModelMatrix.
			const suffix: string = DynamicFieldTable.getSuffix(rowIndex + 1);
			for (const meta of this.fieldMeta) {
				const proposedFieldName = meta.fieldNamePrefix + suffix;

				// Does this field name exist in our model already?
				if (
					fieldModelMatrix.some(
						(fieldModel) =>
							fieldModel[meta.fieldNamePrefix].name ===
							proposedFieldName,
					)
				) {
					isRowAlreadyInFieldModel = true;
				}
			}

			if (isRowAlreadyInFieldModel === false) {
				// This is a free row, we didn't find any existing rows that match the one we want to add.
				this.pushRecord(fieldModelMatrix, rowIndex);
				return rowIndex;
			}
		}
		throw Error(`Tried to add a row, but the model is already full.`);
	}

	/**
	 * make sure there is at least one record
	 */
	private popRecord(): void {
		if (this.fieldModelMatrix.length > 1) {
			this.removeRow(this.fieldModelMatrix.length - 1);
		}
	}

	/**
	 * remove record at row. It copy value iteratively from next record and then remove the last record
	 */
	protected onClickRemoveRecord(e, row: number): void {
		if (this.fieldModelMatrix.length <= 1) {
			return;
		}
		this.removeRow(row);
	}

	/**
	 * Copy field model value iteratively from next row, empty the last field model and then pop the last field model row
	 */
	private removeRow(row: number) {
		if (row >= 0 && row < this.fieldModelMatrix.length - 1) {
			for (
				let fieldIndex = row;
				fieldIndex < this.fieldModelMatrix.length - 1;
				fieldIndex++
			) {
				this.copyFieldModelValue(fieldIndex + 1, fieldIndex);
			}
		}
		for (const prefix of this.fieldMeta) {
			const fieldModel = DynamicFieldTable.getFieldModel(
				this.formModel,
				prefix.fieldNamePrefix,
				this.fieldModelMatrix.length - 1,
			);
			if (!fieldModel || fieldModel.value.isBlank()) {
				continue;
			}
			fieldModel.value = getZeroValueForDataType(
				fieldModel.dataType.name,
			);
		}
		this.fieldModelMatrix.pop();
	}

	private copyFieldModelValue(sourceRow: number, destRow: number) {
		for (const prefix of this.fieldMeta) {
			this.fieldModelMatrix[destRow][prefix.fieldNamePrefix].value =
				DynamicFieldTable.getFieldModel(
					this.formModel,
					prefix.fieldNamePrefix,
					sourceRow,
				).value;
		}
	}

	/**
	 * return true if every field name in matrix can be found in `this.formModel`
	 * check `this.formModel` existence first before calling this method
	 * @param fieldNameMatrix
	 */
	private isValidFieldNameMatrix(fieldNameMatrix: string[][]): boolean {
		return (
			fieldNameMatrix.findIndex(
				(row) =>
					row.findIndex((col) => !!this.formModel.fields[col]) === -1,
			) === -1
		);
	}

	private isEmptyRow(fieldNames: string[]): boolean {
		let emptyFieldCount = 0;
		for (const fieldName of fieldNames) {
			const fieldModel = this.formModel.fields[fieldName];
			if (!fieldModel) {
				emptyFieldCount++;
				continue;
			}

			// Check if field is empty
			{
				let forceFieldEmpty = false;
				for (const meta of this.fieldMeta) {
					if (
						meta.forceIsEmpty &&
						fieldName.startsWith(meta.fieldNamePrefix)
					) {
						forceFieldEmpty = true;
						break;
					}
				}
				if (forceFieldEmpty) {
					emptyFieldCount++;
					continue;
				}
			}

			const isFieldEmpty: boolean =
				!fieldModel.value ||
				fieldModel.value.trim() === '' ||
				fieldModel.value ===
					getZeroValueForDataType(fieldModel.dataType.name);
			if (isFieldEmpty) {
				emptyFieldCount++;
				continue;
			}
		}

		return emptyFieldCount === fieldNames.length;
	}

	public static getSuffix(rowNum: number) {
		return rowNum < 10 ? '0' + rowNum : String(rowNum);
	}

	public static getRowIndexFromSuffixOfFieldName(fieldName: string) {
		return parseInt(fieldName.slice(-2), 10);
	}

	public onSearchSelect = (
		record: { [columnName: string]: any },
		value: string,
		fieldModel: FieldModel,
	): void => {
		const [rowIndex, fieldIndex] = this.getFieldModelRowIndex(fieldModel);
		if (rowIndex !== -1 && fieldIndex !== -1) {
			const fieldMeta = this.fieldMeta[fieldIndex];
			if (fieldMeta.onSearchSelect) {
				fieldMeta.onSearchSelect(
					rowIndex,
					record,
					value,
					this.fieldModelMatrix.length,
				);
			}
		}
	};

	/**
	 * return rowIndex and fieldIndex of fieldModel parameter in
	 * fieldmodelMatrix
	 * @param fieldModel
	 */
	protected getFieldModelRowIndex = (
		fieldModel: FieldModel,
	): [number, number] => {
		let rowIndex = 0;
		for (const row of this.fieldModelMatrix) {
			let fieldIndex = 0;
			for (const element of Object.values(row)) {
				if (element === fieldModel) {
					return [rowIndex, fieldIndex];
				}
				fieldIndex++;
			}
			rowIndex++;
		}
		return [-1, -1];
	};
}
